import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Pencil, Loader2, AlertTriangle, Send, X, Ban } from "lucide-react";
import { aiApi, absUrl } from "./aiApi";

const STEPS = ["Menyusun idea...", "Melakar reka bentuk...", "Mewarna gambar...", "Kemasan akhir..."];

export default function AIImageJobCard({ jobId, onNewJob, onPreview }) {
  const [job, setJob] = useState(null);
  const [step, setStep] = useState(0);
  const [editing, setEditing] = useState(false);
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    let t;
    const poll = () => aiApi.imageJob(jobId).then((j) => {
      if (!alive) return;
      setJob(j);
      if (j.status === "queued" || j.status === "running") t = setTimeout(poll, 3000);
    }).catch(() => alive && setJob({ status: "failed", error: "Gagal memuat status gambar" }));
    poll();
    const s = setInterval(() => setStep((i) => (i + 1) % STEPS.length), 2500);
    return () => { alive = false; clearTimeout(t); clearInterval(s); };
  }, [jobId]);

  const follow = async (fn) => {
    setBusy(true);
    try { const j = await fn(); onNewJob && onNewJob(j.id); setEditing(false); setInstr(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };

  const pending = !job || job.status === "queued" || job.status === "running";
  const cancel = async () => {
    setBusy(true);
    try { setJob(await aiApi.cancelImage(jobId)); toast.success("Penjanaan gambar dibatalkan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membatalkan"); } finally { setBusy(false); }
  };
  const PROV = { gemini: "Gemini", openai: "OpenAI", ai_model: "Model AI", claude: "Claude", byta: "Byta AI" };
  const ratio = { "1:1": "1 / 1", "9:16": "9 / 16", "16:9": "16 / 9", A4: "1 / 1.414" }[job?.size] || "1 / 1";
  return (
    <div className="mt-2 rounded-2xl border border-slate-100 bg-slate-50/60 p-2 max-w-[320px]" data-testid="ai-image-job-card" data-status={job?.status || "loading"}>
      <div className="relative w-full overflow-hidden rounded-xl bg-white" style={{ aspectRatio: ratio }}>
        {pending && (
          <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-pink-50 via-amber-50 to-sky-50" data-testid="ai-image-job-loading">
            <div className="text-center"><Loader2 className="h-7 w-7 mx-auto animate-spin text-pink-500" /><div className="mt-2 text-xs font-semibold text-slate-600">{STEPS[step]}</div>
              {job && <button disabled={busy} onClick={cancel} className="mt-3 h-8 px-3 rounded-full bg-white/90 border border-rose-200 text-xs font-semibold text-rose-600 inline-flex items-center gap-1 hover:bg-rose-50 disabled:opacity-50" data-testid="ai-image-cancel-btn">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Batal</button>}
            </div>
          </div>
        )}
        {job?.status === "done" && (
          <button type="button" className="block w-full h-full" onClick={() => onPreview && onPreview(absUrl(job.url))} data-testid="ai-image-job-preview">
            <img src={absUrl(job.url)} alt={job.prompt} className="w-full h-full object-cover" />
          </button>
        )}
        {job?.status === "cancelled" && (
          <div className="absolute inset-0 grid place-items-center p-3 text-center bg-slate-50" data-testid="ai-image-job-cancelled">
            <div><Ban className="h-6 w-6 mx-auto text-slate-400" /><div className="mt-1 text-[11.5px] text-slate-500">Dibatalkan</div></div>
          </div>
        )}
        {job?.status === "failed" && (
          <div className="absolute inset-0 grid place-items-center p-3 text-center" data-testid="ai-image-job-error">
            <div><AlertTriangle className="h-6 w-6 mx-auto text-rose-500" /><div className="mt-1 text-[11.5px] text-rose-600">{job.error}</div></div>
          </div>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-1 text-[10.5px] text-slate-500">
        <span className="font-bold uppercase tracking-wide">{job?.size}</span>{job?.provider && <span>· {PROV[job.provider] || job.provider}</span>}{job?.format === "jpeg" && <span>· JPG</span>}
        {job?.mode && job.mode !== "generate" && <span>· {job.mode === "edit" ? "Diedit" : "Dijana semula"}</span>}
      </div>
      {job?.note && <div className="mt-1 text-[10.5px] text-amber-600" data-testid="ai-image-job-note">{job.note}</div>}
      {!pending && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {job.status === "done" && (
            <a href={absUrl(job.download_url)} className="h-8 px-3 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700 inline-flex items-center gap-1 hover:bg-slate-50" data-testid="ai-image-download-btn"><Download className="h-3.5 w-3.5" /> Muat Turun</a>
          )}
          <button disabled={busy} onClick={() => follow(() => aiApi.regenImage(jobId))} className="h-8 px-3 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700 inline-flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50" data-testid="ai-image-regenerate-btn"><RefreshCw className="h-3.5 w-3.5" /> Jana Semula</button>
          {job.status === "done" && (
            <button disabled={busy} onClick={() => setEditing((v) => !v)} className="h-8 px-3 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700 inline-flex items-center gap-1 hover:bg-slate-50" data-testid="ai-image-edit-btn"><Pencil className="h-3.5 w-3.5" /> Edit</button>
          )}
        </div>
      )}
      {editing && (
        <div className="mt-1.5 flex gap-1">
          <input value={instr} onChange={(e) => setInstr(e.target.value)} placeholder="Cth: tukar latar jadi biru, tambah logo" className="flex-1 h-8 rounded-full border border-slate-200 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-pink-100" data-testid="ai-image-edit-input" />
          <button disabled={busy || instr.trim().length < 3} onClick={() => follow(() => aiApi.editImage(jobId, instr.trim()))} className="h-8 w-8 rounded-full bg-pink-500 text-white grid place-items-center disabled:opacity-40" data-testid="ai-image-edit-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}</button>
        </div>
      )}
    </div>
  );
}
